import { useState, useEffect } from "react";
import { useTranslation } from "next-i18next";
import { Modal, Spin } from "antd";
import NextImage from "next/image";
import { IProduct } from "@/props/IProduct";
import { useRouter } from "next/router";
import { copyText } from "@/utils/index";
import Icon from "../Icon";
import BASE_PATH from "@/config/basePath";
import { DownloadOutlined, CopyOutlined } from "@ant-design/icons";
const QRCode = require("qrcode");

/**
 * 商品分享
 * @param open 是否显示
 * @param product 商品信息
 * @param onModalCancel 关闭 modal
 */
const ProductShareModal = ({
  open,
  product,
  onModalCancel,
}: {
  open: boolean;
  product: Pick<IProduct, "id" | "image" | "storeName" | "price">;
  onModalCancel: (open: boolean) => void;
}) => {
  const { t } = useTranslation();
  const router = useRouter();
  const currentLocale: string | undefined = router.locale?.toUpperCase() || "EN";
  const defaultLocale: string | undefined = router.defaultLocale?.toUpperCase() || "EN";
  const [shareImgUrl, setShareImgUrl] = useState<string>("");
  const [accessLink, setAccessLink] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);

  /** 设置访问链接 */
  const handleAccessLink = () => {
    return new Promise((resolve) => {
      let link = window.location.origin;
      if (currentLocale === defaultLocale) {
        link += "/mall/" + product.id;
      } else {
        link += "/" + currentLocale.toLowerCase() + "/mall/" + product.id;
      }
      setAccessLink(link);
      resolve(link);
    });
  };

  /** 加载图片 */
  const createImage = (width: number, height: number, src: string) => {
    return new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image(width, height);
      img.setAttribute("crossOrigin", "anonymous");
      img.src = src;
      img.onload = () => resolve(img);
      img.onerror = reject;
    });
  };

  /** 生成二维码 */
  const createQrcode = async () => {
    const link = await handleAccessLink();
    return new Promise<HTMLImageElement>((resolve) => {
      QRCode.toDataURL(link, { margin: 1, width: 120 }, (err: any, url: any) => {
        if (err) console.log(err);
        const img = new Image(80, 80);
        img.setAttribute("crossOrigin", "anonymous");
        img.src = url;
        img.onload = () => resolve(img);
      });
    });
  };

  /** 绘制圆角矩形 */
  const roundRect = (ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) => {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
  };

  /** 创建分享海报 canvas */
  const handleCreateCanvas = async () => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const W = 400;
    const cardH = 320;
    const bottomH = 80;
    const totalH = cardH + bottomH;
    canvas.width = W;
    canvas.height = totalH;

    // ——— 红色渐变卡片背景 ———
    const grad = ctx.createLinearGradient(0, 0, W, cardH);
    grad.addColorStop(0, "#c62828");
    grad.addColorStop(0.5, "#e53935");
    grad.addColorStop(1, "#b71c1c");
    ctx.fillStyle = grad;
    // 左上右上圆角，左下右下直角
    const cr = 12;
    ctx.beginPath();
    ctx.moveTo(cr, 0);
    ctx.lineTo(W - cr, 0);
    ctx.arcTo(W, 0, W, cr, cr);
    ctx.lineTo(W, cardH);
    ctx.lineTo(0, cardH);
    ctx.lineTo(0, cr);
    ctx.arcTo(0, 0, cr, 0, cr);
    ctx.closePath();
    ctx.fill();

    // 装饰圆（半透明）
    ctx.fillStyle = "rgba(255,255,255,0.06)";
    ctx.beginPath();
    ctx.arc(W * 0.7, cardH * 0.25, 120, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.arc(W * 0.3, cardH * 0.75, 90, 0, Math.PI * 2);
    ctx.fill();

    // ——— Logo ———
    try {
      // createImage 走原生 new Image()，Next 不会自动补 basePath，漏了必 404
      const logo = await createImage(119, 40, `${BASE_PATH}/images/logo_white.webp`);
      ctx.drawImage(logo, 20, 18, 119, 40);
    } catch {}

    // ——— 商品图片（居中，半透明底） ———
    const imgSize = 190;
    const imgX = (W - imgSize) / 2;
    const imgY = 65;

    // 半透明圆角背景
    ctx.fillStyle = "rgba(255,255,255,0.12)";
    roundRect(ctx, imgX - 10, imgY - 10, imgSize + 20, imgSize + 20, 16);
    ctx.fill();

    try {
      const productImg = await createImage(imgSize, imgSize, product.image[currentLocale]);
      ctx.drawImage(productImg, imgX, imgY, imgSize, imgSize);
    } catch {
      // 图片加载失败
    }

    // ——— 品牌标语（自动换行） ———
    ctx.fillStyle = "rgba(255,255,255,0.85)";
    ctx.font = "13px Arial";
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    const brandText = t("product_detail.share_brand_identity") || "";
    const maxTextW = W - 90;
    const lineHeight = 18;
    // 按字符逐步分行，不限行数
    const lines: string[] = [];
    let currentLine = "";
    for (let i = 0; i < brandText.length; i++) {
      const testLine = currentLine + brandText[i];
      if (ctx.measureText(testLine).width > maxTextW) {
        lines.push(currentLine);
        currentLine = brandText[i];
      } else {
        currentLine = testLine;
      }
    }
    if (currentLine) lines.push(currentLine);
    const textStartY = imgY + imgSize + 16;
    lines.forEach((line, idx) => {
      ctx.fillText(line, W / 2, textStartY + idx * lineHeight);
    });

    // ——— 白色底部：商品名 + 二维码（左上右上直角，左下右下圆角） ———
    ctx.fillStyle = "#ffffff";
    const r = 12;
    ctx.beginPath();
    ctx.moveTo(0, cardH);
    ctx.lineTo(W, cardH);
    ctx.lineTo(W, cardH + bottomH - r);
    ctx.arcTo(W, cardH + bottomH, W - r, cardH + bottomH, r);
    ctx.lineTo(r, cardH + bottomH);
    ctx.arcTo(0, cardH + bottomH, 0, cardH + bottomH - r, r);
    ctx.closePath();
    ctx.fill();

    // 商品名（左侧，限制宽度）
    ctx.fillStyle = "#1a1a1a";
    ctx.font = "bold 14px Arial";
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    const name = product.storeName[currentLocale] || "";
    const maxNameW = W - 100;
    let displayName = name;
    while (ctx.measureText(displayName).width > maxNameW && displayName.length > 0) {
      displayName = displayName.slice(0, -1);
    }
    if (displayName.length < name.length) displayName += "...";
    ctx.fillText(displayName, 20, cardH + bottomH / 2);

    // 二维码（右侧）
    try {
      const qrImg = await createQrcode();
      const qrSize = 56;
      ctx.drawImage(qrImg, W - qrSize - 16, cardH + (bottomH - qrSize) / 2, qrSize, qrSize);
    } catch {
      // qrcode 失败
    }

    const url = canvas.toDataURL();
    setShareImgUrl(url);
    setTimeout(() => setLoading(false), 200);
  };

  /** 下载图片 */
  const handleDownloadImage = () => {
    const a = document.createElement("a");
    a.download = product.storeName[currentLocale] || "share";
    a.href = shareImgUrl;
    a.click();
  };

  useEffect(() => {
    if (open) {
      setLoading(true);
      handleAccessLink();
      handleCreateCanvas();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Modal
      title={t("product_detail.share")}
      open={open}
      footer={null}
      width={420}
      onCancel={() => onModalCancel(false)}
      closeIcon={<Icon name="close" size={20} title="icon close" />}
      maskClosable={false}
      className="product-share-modal"
      centered
    >
      <div className="flex flex-col items-center pt-4">
        {/* 海报卡片 */}
        <div className="mb-5 w-full overflow-hidden rounded-xl shadow-lg">
          <Spin spinning={loading}>
            <div className="aspect-[400/400] w-full bg-gray-100">
              {shareImgUrl && (
                <NextImage
                  src={shareImgUrl}
                  alt="share"
                  title="share"
                  width={400}
                  height={400}
                  className="h-full w-full"
                />
              )}
            </div>
          </Spin>
        </div>

        {/* 分享链接 */}
        <div className="w-full">
          <div className="mb-2 text-xs text-stone-500">{t("product_detail.access_link")}</div>
          <div className="bg-gary-300 flex items-center overflow-hidden rounded-lg border border-gray-600 bg-gray-300">
            <div className="flex-1 truncate px-4 py-4 text-sm text-stone-500">{accessLink}</div>
            <div
              className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border border-gray-600 bg-white transition-colors hover:border-primary hover:bg-[#fde4e1]"
              onClick={handleDownloadImage}
              title={t("common.download")}
            >
              <DownloadOutlined />
            </div>
            <div
              className="mx-2 flex h-8 w-8 cursor-pointer items-center justify-center rounded-md  border border-gray-600 bg-white transition-colors hover:border-primary hover:bg-[#fde4e1]"
              onClick={() => copyText(accessLink, t("common.copy_success"), t("common.copy_fail"))}
              title={t("common.copy")}
            >
              <CopyOutlined />
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ProductShareModal;
